import { z } from 'zod';

import { isDefined } from '../../../../utils.js';
import {
  BaseFlowBlockSchema,
  BlockType,
  ProducedType,
  returnTypeSchema,
} from '../../base/baseSchema.js';
import { FlowVersionTarget } from '../../flowExecution.js';

export const SubFlowV1BlockConfigSchema = z
  .object({
    // versionTarget and flowId are optional solely to be backwards compatible
    versionTarget: z.nativeEnum(FlowVersionTarget).optional(),
    flowId: z.string().uuid('A valid flow ID is required').optional(),

    flowDefinitionId: z.string().uuid('A valid flow definition ID is required').optional(),
    startDataBlockName: z.string().optional(),
  })
  .refine(
    (obj) => {
      if (obj.versionTarget === undefined) {
        return true; // Support legacy block definitions which didn't specify this field
      }
      switch (obj.versionTarget) {
        case FlowVersionTarget.SpecificVersion:
          return isDefined(obj.flowDefinitionId) && obj.flowDefinitionId.length > 0;
        case FlowVersionTarget.Published:
          return isDefined(obj.flowId) && obj.flowId.length > 0;
      }
    },
    (obj) => {
      if (obj.versionTarget === FlowVersionTarget.SpecificVersion) {
        return {
          message: `A flow definition must be selected when using versionTarget=${obj.versionTarget}`,
          path: ['flowDefinitionId'],
        };
      }
      if (obj.versionTarget === FlowVersionTarget.Published) {
        return {
          message: `A flow must be selected when using versionTarget=${obj.versionTarget}`,
          path: ['flowId'],
        };
      }
      return {
        message: `You must select a version target`,
        path: ['versionTarget'],
      };
    }
  );

export type SubFlowV1BlockConfig = z.infer<typeof SubFlowV1BlockConfigSchema>;

export const SubFlowV1BlockSchema = BaseFlowBlockSchema.extend({
  type: z.literal(BlockType.SubFlowV1),
  returnType: returnTypeSchema(ProducedType.SubFlowV1Result),
  config: SubFlowV1BlockConfigSchema,
});

export type SubFlowV1Block = z.infer<typeof SubFlowV1BlockSchema>;
